import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, ChevronUp, ChevronDown, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, money, dateShort, productImage } from "@/lib/api";
import { Thumb, ProcurementBadge, SelectionBadge, EmptyState } from "@/components/Bits";

const ALL_COLUMNS = [
  { key: "image", label: "Image" },
  { key: "product", label: "Product" },
  { key: "project", label: "Project" },
  { key: "room", label: "Room" },
  { key: "vendor", label: "Vendor" },
  { key: "sku", label: "SKU" },
  { key: "qty", label: "Qty" },
  { key: "client_price", label: "Client Price" },
  { key: "designer_cost", label: "Designer Cost" },
  { key: "selection", label: "Approval" },
  { key: "status", label: "Order Status" },
  { key: "order_date", label: "Order Date" },
  { key: "est_ship", label: "Est. Ship" },
  { key: "est_delivery", label: "Est. Delivery" },
  { key: "tracking", label: "Tracking" },
  { key: "assigned", label: "Assigned To" },
];

const PROC_STATUSES = ["Not Ready", "Ready to Order", "Ordered", "Acknowledged", "Backordered",
  "In Production", "Ready to Ship", "Shipped", "Partially Delivered", "Delivered", "Damaged",
  "Return Requested", "Returned", "Installed", "Cancelled"];

const QUICK = [
  ["Mark Ordered", "Ordered"],
  ["Mark Shipped", "Shipped"],
  ["Mark Delivered", "Delivered"],
  ["Mark Installed", "Installed"],
];

const cellValue = (row, key) => {
  const p = row.product || {};
  switch (key) {
    case "product": return p.name || "";
    case "project": return p.project_name || "";
    case "room": return p.space_name || "";
    case "vendor": return p.vendor_name || "";
    case "sku": return p.sku || "";
    case "qty": return p.quantity || 0;
    case "client_price": return p.client_total || 0;
    case "designer_cost": return p.designer_total_cost || 0;
    case "selection": return p.selection_status || "";
    case "status": return row.status || "";
    case "order_date": return row.order_date || "";
    case "est_ship": return row.est_ship_date || "";
    case "est_delivery": return row.est_delivery_date || "";
    case "tracking": return row.tracking_number || "";
    case "assigned": return row.assigned_name || "";
    default: return "";
  }
};

export default function ProcurementTable({ projectId, embedded }) {
  const [urlParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({ projects: [], vendors: [], clients: [], spaces: [] });
  const [visible, setVisible] = useState(ALL_COLUMNS.map((c) => c.key));
  const [showCols, setShowCols] = useState(false);
  const [sort, setSort] = useState({ key: "product", dir: 1 });
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    q: "", project_id: projectId || "", client_id: "", space_id: "", vendor_id: "",
    category: "", status: urlParams.get("status") || "",
    selection_status: urlParams.get("selection") || "", assigned_to: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      if (projectId) params.project_id = projectId;
      const { data } = await api.get("/procurement", { params });
      setRows(data);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters), projectId]);

  useEffect(() => {
    Promise.all([
      api.get("/projects").catch(() => ({ data: [] })),
      api.get("/vendors").catch(() => ({ data: [] })),
      api.get("/clients").catch(() => ({ data: [] })),
      api.get("/auth/users").catch(() => ({ data: [] })),
      api.get("/meta/enums").catch(() => ({ data: {} })),
    ]).then(([p, v, c, u, e]) => {
      setMeta({ projects: p.data, vendors: v.data, clients: c.data, categories: e.data.categories || [] });
      setUsers(u.data);
    });
  }, []);

  const userName = (id) => users.find((u) => u.id === id)?.name || "—";

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = cellValue(a, sort.key);
      const bv = cellValue(b, sort.key);
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * sort.dir;
      return String(av).localeCompare(String(bv)) * sort.dir;
    });
    return copy;
  }, [rows, sort]);

  const patch = async (id, body) => {
    try {
      await api.patch(`/procurement/${id}`, body);
      toast.success("Procurement updated");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const bulk = async (status) => {
    if (!selected.length) return;
    try {
      await api.post("/procurement/bulk", { ids: selected, status });
      toast.success(`${selected.length} item(s) → ${status}`);
      setSelected([]);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const has = (k) => visible.includes(k);
  const th = (key, label, numeric) => (
    <th key={key} className="whitespace-nowrap border-b border-line px-4 py-3">
      <button
        data-testid={`sort-${key}`}
        onClick={() => setSort((s) => ({ key, dir: s.key === key ? -s.dir : 1 }))}
        className={`overline inline-flex items-center gap-1 hover:text-ink ${numeric ? "justify-end" : ""}`}
      >
        {label}
        {sort.key === key && (sort.dir === 1 ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
      </button>
    </th>
  );

  const selectClass = "rounded-sm border border-line bg-white px-3 py-2 text-xs text-stone outline-none focus:border-lime";

  return (
    <div className="space-y-5" data-testid="procurement-table-wrap">
      <div className="flex flex-wrap items-end gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fog" strokeWidth={1.5} />
          <input
            data-testid="procurement-search-input"
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            placeholder="Search product, SKU, vendor, project, PO…"
            className="w-full rounded-sm border border-line bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-lime"
          />
        </div>
        {!projectId && (
          <select data-testid="procurement-filter-project" className={selectClass} value={filters.project_id} onChange={(e) => setFilters((f) => ({ ...f, project_id: e.target.value }))}>
            <option value="">All projects</option>
            {meta.projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        {!projectId && (
          <select data-testid="procurement-filter-client" className={selectClass} value={filters.client_id} onChange={(e) => setFilters((f) => ({ ...f, client_id: e.target.value }))}>
            <option value="">All clients</option>
            {meta.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <select data-testid="procurement-filter-vendor" className={selectClass} value={filters.vendor_id} onChange={(e) => setFilters((f) => ({ ...f, vendor_id: e.target.value }))}>
          <option value="">All vendors</option>
          {meta.vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select data-testid="procurement-filter-category" className={selectClass} value={filters.category} onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}>
          <option value="">All categories</option>
          {(meta.categories || []).map((c) => <option key={c}>{c}</option>)}
        </select>
        <select data-testid="procurement-filter-status" className={selectClass} value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
          <option value="">All order statuses</option>
          {PROC_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select data-testid="procurement-filter-selection" className={selectClass} value={filters.selection_status} onChange={(e) => setFilters((f) => ({ ...f, selection_status: e.target.value }))}>
          <option value="">All approval statuses</option>
          {["Considering", "Shortlisted", "Presented", "Awaiting Approval", "Approved", "Revision Requested", "Declined"].map((s) => <option key={s}>{s}</option>)}
        </select>
        <select data-testid="procurement-filter-assigned" className={selectClass} value={filters.assigned_to} onChange={(e) => setFilters((f) => ({ ...f, assigned_to: e.target.value }))}>
          <option value="">Anyone</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <button
          data-testid="procurement-columns-btn"
          onClick={() => setShowCols((s) => !s)}
          className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-3 py-2 text-xs text-stone transition-colors hover:border-fog"
        >
          <SlidersHorizontal className="h-3.5 w-3.5" strokeWidth={1.5} /> Columns
        </button>
      </div>

      {showCols && (
        <div className="card-surface flex flex-wrap gap-3 p-5" data-testid="procurement-column-toggles">
          {ALL_COLUMNS.map((c) => (
            <label key={c.key} className="inline-flex items-center gap-2 text-xs text-stone">
              <input
                type="checkbox"
                checked={has(c.key)}
                onChange={() =>
                  setVisible((v) => (v.includes(c.key) ? v.filter((k) => k !== c.key) : [...v, c.key]))
                }
                className="h-3.5 w-3.5 accent-[#BEC221]"
              />
              {c.label}
            </label>
          ))}
        </div>
      )}

      {selected.length > 0 && (
        <div className="card-surface flex flex-wrap items-center gap-3 p-4" data-testid="procurement-bulk-bar">
          <span className="num text-sm text-ink">{selected.length} selected</span>
          {QUICK.map(([label, status]) => (
            <button
              key={status}
              data-testid={`bulk-${status.toLowerCase()}`}
              onClick={() => bulk(status)}
              className="rounded-sm border border-line bg-white px-3 py-1.5 text-xs text-ink transition-colors hover:border-lime hover:bg-[#EDEFC4]"
            >
              {label}
            </button>
          ))}
          <button onClick={() => setSelected([])} className="ml-auto inline-flex items-center gap-1 text-xs text-stone hover:text-ink">
            <X className="h-3.5 w-3.5" /> Clear
          </button>
        </div>
      )}

      {loading ? (
        <div className="card-surface px-6 py-16 text-center text-sm text-fog">Loading procurement…</div>
      ) : sorted.length === 0 ? (
        <EmptyState testId="procurement-empty" title="No procurement items" body="Add products to a project to build the procurement queue." />
      ) : (
        <div className="card-surface overflow-x-auto scroll-thin" data-testid="procurement-table">
          <table className="w-full min-w-[1100px] text-sm">
            <thead>
              <tr className="text-left">
                <th className="border-b border-line px-4 py-3">
                  <input
                    type="checkbox"
                    data-testid="procurement-select-all"
                    checked={selected.length === sorted.length}
                    onChange={(e) => setSelected(e.target.checked ? sorted.map((r) => r.id) : [])}
                    className="h-3.5 w-3.5 accent-[#BEC221]"
                  />
                </th>
                {has("image") && <th className="border-b border-line px-4 py-3"><span className="overline">Image</span></th>}
                {has("product") && th("product", "Product")}
                {has("project") && !projectId && th("project", "Project")}
                {has("room") && th("room", "Room")}
                {has("vendor") && th("vendor", "Vendor")}
                {has("sku") && th("sku", "SKU")}
                {has("qty") && th("qty", "Qty")}
                {has("client_price") && th("client_price", "Client Price")}
                {has("designer_cost") && th("designer_cost", "Designer Cost")}
                {has("selection") && th("selection", "Approval")}
                {has("status") && th("status", "Order Status")}
                {has("order_date") && th("order_date", "Order Date")}
                {has("est_ship") && th("est_ship", "Est. Ship")}
                {has("est_delivery") && th("est_delivery", "Est. Delivery")}
                {has("tracking") && th("tracking", "Tracking")}
                {has("assigned") && th("assigned", "Assigned To")}
                <th className="border-b border-line px-4 py-3"><span className="overline">Quick</span></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => {
                const p = row.product || {};
                return (
                  <tr key={row.id} data-testid={`procurement-row-${row.id}`} className="border-b border-line transition-colors last:border-0 hover:bg-bone">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        data-testid={`procurement-select-${row.id}`}
                        checked={selected.includes(row.id)}
                        onChange={(e) =>
                          setSelected((s) => (e.target.checked ? [...s, row.id] : s.filter((x) => x !== row.id)))
                        }
                        className="h-3.5 w-3.5 accent-[#BEC221]"
                      />
                    </td>
                    {has("image") && (
                      <td className="px-4 py-3">
                        <Link to={`/procurement/${row.id}`}><Thumb src={productImage(p)} className="h-11 w-14" /></Link>
                      </td>
                    )}
                    {has("product") && (
                      <td className="px-4 py-3">
                        <Link to={`/procurement/${row.id}`} className="font-medium text-ink hover:text-lime-dark">{p.name}</Link>
                        <div className="text-xs text-fog">{p.category}</div>
                      </td>
                    )}
                    {has("project") && !projectId && <td className="px-4 py-3 text-stone">{p.project_name || "—"}</td>}
                    {has("room") && <td className="px-4 py-3 text-stone">{p.space_name || "—"}</td>}
                    {has("vendor") && <td className="px-4 py-3 text-stone">{p.vendor_name || "—"}</td>}
                    {has("sku") && <td className="num px-4 py-3 text-xs text-stone">{p.sku || "—"}</td>}
                    {has("qty") && <td className="num px-4 py-3 text-right">{p.quantity}</td>}
                    {has("client_price") && <td className="num px-4 py-3 text-right">{money(p.client_total)}</td>}
                    {has("designer_cost") && <td className="num px-4 py-3 text-right text-stone">{money(p.designer_total_cost)}</td>}
                    {has("selection") && <td className="px-4 py-3"><SelectionBadge status={p.selection_status} /></td>}
                    {has("status") && (
                      <td className="px-4 py-3">
                        <select
                          data-testid={`procurement-status-select-${row.id}`}
                          value={row.status}
                          onChange={(e) => patch(row.id, { status: e.target.value })}
                          className="rounded-sm border border-line bg-white px-2 py-1.5 text-xs outline-none focus:border-lime"
                        >
                          {PROC_STATUSES.map((s) => <option key={s}>{s}</option>)}
                        </select>
                      </td>
                    )}
                    {has("order_date") && <td className="num px-4 py-3 text-xs text-stone">{dateShort(row.order_date)}</td>}
                    {has("est_ship") && <td className="num px-4 py-3 text-xs text-stone">{dateShort(row.est_ship_date)}</td>}
                    {has("est_delivery") && <td className="num px-4 py-3 text-xs text-stone">{dateShort(row.est_delivery_date)}</td>}
                    {has("tracking") && (
                      <td className="num px-4 py-3 text-xs">
                        {row.tracking_number ? (
                          row.tracking_url ? (
                            <a href={row.tracking_url} target="_blank" rel="noreferrer" className="text-lime-dark hover:underline">{row.tracking_number}</a>
                          ) : row.tracking_number
                        ) : "—"}
                      </td>
                    )}
                    {has("assigned") && <td className="px-4 py-3 text-xs text-stone">{userName(row.assigned_to)}</td>}
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        {QUICK.map(([label, status]) => (
                          <button
                            key={status}
                            data-testid={`quick-${status.toLowerCase()}-${row.id}`}
                            title={label}
                            onClick={() => patch(row.id, { status })}
                            className="rounded-sm border border-line px-2 py-1 text-[10px] uppercase tracking-wide text-stone transition-colors hover:border-lime hover:bg-[#EDEFC4] hover:text-ink"
                          >
                            {status.slice(0, 4)}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!embedded && sorted.length > 0 && (
        <div className="flex flex-wrap gap-6 text-xs text-fog">
          <span className="num">{sorted.length} items</span>
          <span className="num">Client total {money(sorted.reduce((a, r) => a + (r.product?.client_total || 0), 0))}</span>
          <span className="num">Designer cost {money(sorted.reduce((a, r) => a + (r.product?.designer_total_cost || 0), 0))}</span>
        </div>
      )}
    </div>
  );
}

export { ProcurementBadge };
